import {useQueryClient} from "@tanstack/react-query";
import {useCallback, useEffect, useState} from "react";

import {BookOpen} from "lucide-react";

import {Badge} from "@/components/ui/badge";
import {Dialog, DialogContent} from "@/components/ui/dialog";
import {cn} from "@/lib/utils";
import {
  ComplianceGateDialogHeader,
  ComplianceReconcileOverlay,
  ModalCreatedFooter,
  complianceGateDialogContentClassName,
} from "@/src/modules/core/components";
import {useBlockingGate} from "@/src/modules/core/hooks/use-blocking-gate";
import {BriefingRecipientContent} from "@/src/modules/briefing-boards/components/briefing-recipient-content";
import {useSectionPermissions} from "@/src/modules/core/hooks";
import {isViewingAsUser} from "@/src/modules/core/lib/impersonation";
import {reconcileCompliance} from "@/src/modules/core/lib/reconcile-compliance";
import {useStoredMe} from "@/src/modules/core/store/me";
import type {GetListResponse} from "@/src/modules/core/types";
import useBriefingBoardRead from "@/src/modules/briefing-boards/hooks/use-briefing-board-read";
import {useBriefingPendingRealtime} from "@/src/modules/briefing-boards/hooks/use-briefing-pending-realtime";
import usePendingBriefings from "@/src/modules/briefing-boards/hooks/use-pending-briefings";
import {BRIEF_GATE_SUBTITLE} from "@/src/modules/briefing-boards/lib/brief-display";
import type {BriefingBoardAcknowledgment} from "@/src/modules/briefing-boards/types";
import {useActiveShift} from "@/src/modules/officer/hooks/use-active-shift";

function removeAcknowledgmentFromPendingCache(
  queryClient: ReturnType<typeof useQueryClient>,
  acknowledgmentId: number,
) {
  queryClient.setQueriesData<GetListResponse<BriefingBoardAcknowledgment>>(
    {queryKey: ["briefingboard-pending"]},
    (old) => {
      if (!old?.data) {
        return old;
      }
      const data = old.data.filter((row) => row.acknowledgment_id !== acknowledgmentId);
      return {
        ...old,
        data,
        meta: {
          ...old.meta,
          total: data.length,
        },
      };
    },
  );
}

export function ForcedReadGate() {
  const queryClient = useQueryClient();
  const me = useStoredMe();
  const viewingAsUser = isViewingAsUser(me);
  const permissionsObject = useSectionPermissions("briefingboard", ["show", "mark_read"]);
  const hasShow = permissionsObject.show;
  const canMarkRead = permissionsObject.mark_read;
  const {shift, isSuccess: isShiftLoaded} = useActiveShift();
  const activeShiftId = shift?.status === "active" ? shift.shift_id : null;
  const gateEnabled =
    hasShow && canMarkRead && !viewingAsUser && isShiftLoaded && activeShiftId != null;

  useBriefingPendingRealtime({enabled: gateEnabled});

  const {briefingBoards, isPending} = usePendingBriefings({
    shiftId: activeShiftId,
    enabled: gateEnabled,
  });
  const [index, setIndex] = useState(0);

  const {mutate: markRead, isPending: isMarkingRead} = useBriefingBoardRead();

  useEffect(() => {
    if (index >= briefingBoards.length) {
      setIndex(0);
    }
  }, [briefingBoards.length, index]);

  const present =
    gateEnabled && !(isPending && briefingBoards.length === 0) && briefingBoards.length > 0;

  const {isVisible, showReconcilingOverlay, runWithReconcile} = useBlockingGate({
    id: "briefing-forced-read",
    present,
  });

  const handleMarkRead = useCallback(
    (briefingBoard: BriefingBoardAcknowledgment) => {
      const acknowledgmentId = briefingBoard.acknowledgment_id;
      markRead(
        {
          id: briefingBoard.briefing_board_id,
          shift_id: briefingBoard.shift_id,
        },
        {
          onSuccess: () => {
            removeAcknowledgmentFromPendingCache(queryClient, acknowledgmentId);
            setIndex((current) => {
              const nextLength = briefingBoards.length - 1;
              if (nextLength <= 0) {
                return 0;
              }
              return Math.min(current, nextLength - 1);
            });
            void runWithReconcile(
              async () => {},
              () => reconcileCompliance(queryClient),
            );
          },
        },
      );
    },
    [briefingBoards.length, markRead, queryClient, runWithReconcile],
  );

  if (!present) {
    return null;
  }

  const briefingBoard = briefingBoards[index];
  if (!briefingBoard) {
    return null;
  }

  const contentKey = briefingBoard.acknowledgment_id;
  const queueLabel =
    briefingBoards.length > 1 ? `${index + 1} of ${briefingBoards.length}` : null;
  const headerDescription = [
    BRIEF_GATE_SUBTITLE,
    queueLabel ? `Briefing ${queueLabel}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Dialog open={isVisible} onOpenChange={() => {}}>
      <DialogContent
        showCloseButton={false}
        data-help-tour-blocker
        className={cn(complianceGateDialogContentClassName, "max-h-[90vh] sm:max-w-[700px]")}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <ComplianceGateDialogHeader
              icon={BookOpen}
              title={`Briefing #${briefingBoard.briefing_board_id}`}
              description={headerDescription}
              className="min-w-0 flex-1"
            />
            <Badge variant="secondary" className="shrink-0 text-xs">
              Unread
            </Badge>
          </div>

          <div key={contentKey} className="flex flex-col gap-4">
            <BriefingRecipientContent
              brief={briefingBoard.brief}
              createdBy={briefingBoard.created_by}
              createdAt={briefingBoard.created_at}
              expiresAt={briefingBoard.expires_at}
              media={briefingBoard.media}
              showReadAction
              isMarkingRead={isMarkingRead}
              onMarkRead={() => handleMarkRead(briefingBoard)}
            />
          </div>

          <ModalCreatedFooter createdAt={briefingBoard.created_at} />
        </div>
        {showReconcilingOverlay ? <ComplianceReconcileOverlay /> : null}
      </DialogContent>
    </Dialog>
  );
}
